// AIOC Web Testing & Diagnostic Utility
// Handles WebHID (CM108), Web Serial Console, RTS/DTR PTT, and Web Audio Rx/Tx

// Morse code lookup table
const MORSE_MAP = {
  A: '.-',
  B: '-...',
  C: '-.-.',
  D: '-..',
  E: '.',
  F: '..-.',
  G: '--.',
  H: '....',
  I: '..',
  J: '.---',
  K: '-.-',
  L: '.-..',
  M: '--',
  N: '-.',
  O: '---',
  P: '.--.',
  Q: '--.-',
  R: '.-.',
  S: '...',
  T: '-',
  U: '..-',
  V: '...-',
  W: '.--',
  X: '-..-',
  Y: '-.--',
  Z: '--..',
  1: '.----',
  2: '..---',
  3: '...--',
  4: '....-',
  5: '.....',
  6: '-....',
  7: '--...',
  8: '---..',
  9: '----.',
  0: '-----',
  ' ': '/',
};

// Global states
let pttMode = 'serial'; // 'serial' or 'hid'
let pttConnected = false;
let txActive = false;

// Shared HID connection (PTT and Pin Monitor share this single device if both use HID)
let sharedHidDevice = null;
let pttHidConnected = false;
let monitorHidConnected = false;

// Shared Serial port (PTT and Console share this single port if both connect via Serial)
let sharedSerialPort = null;
let pttSerialConnected = false;
let consoleSerialConnected = false;

// Serial Console states
let serialConsoleReader = null;

// Tone Generator states
let toneActive = false;
let toneOscillator = null;
let toneGainNode = null;
let morseOscillator = null;

// Audio states
let audioContext = null;
let rxStream = null;
let rxSourceNode = null;
let rxAnalyserNode = null;
let rxMonitorGainNode = null;
let rxAudioConnected = false;
let vuMeterActive = false;
let peakLevel = 0;
let peakHoldFrames = 0;
let audioDevicesEnumerated = false;

// Page elements mapping
let els = {};

// Wait for DOM Content to be fully loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize element selectors
  els = {
    warningBanner: document.getElementById('test-unsupported-warning'),
    pttStatus: document.getElementById('ptt-status'),
    pttAlert: document.getElementById('ptt-alert'),
    pttMode: document.getElementById('select-ptt-mode'),
    groupSerialPtt: document.getElementById('group-serial-ptt'),
    groupHidPtt: document.getElementById('group-hid-ptt'),
    serialPttLine: document.getElementById('select-serial-ptt-line'),
    hidPttPin: document.getElementById('select-hid-ptt-pin'),
    btnConnectPtt: document.getElementById('btn-connect-ptt'),
    pttSetupDetails: document.getElementById('ptt-setup-details'),
    btnPttTrigger: document.getElementById('btn-ptt-trigger'),
    checkPttToggle: document.getElementById('check-ptt-toggle'),
    indicatorTxDot: document.getElementById('indicator-tx-dot'),

    // Audio Output & Tone
    selectAudioOutput: document.getElementById('select-audio-output'),
    sliderToneFreq: document.getElementById('slider-tone-freq'),
    inputToneFreq: document.getElementById('input-tone-freq'),
    btnSendTone: document.getElementById('btn-send-tone'),

    // Morse
    inputMorseMsg: document.getElementById('input-morse-msg'),
    inputMorseWpm: document.getElementById('input-morse-wpm'),
    btnSendMorse: document.getElementById('btn-send-morse'),

    // RX Audio
    rxStatus: document.getElementById('rx-status'),
    rxAlert: document.getElementById('rx-alert'),
    rxSetupDetails: document.getElementById('rx-setup-details'),
    selectAudioInput: document.getElementById('select-audio-input'),
    btnConnectRxAudio: document.getElementById('btn-connect-rx-audio'),
    canvasVuMeter: document.getElementById('canvas-vu-meter'),
    textVuDb: document.getElementById('text-vu-db'),
    checkAudioMonitor: document.getElementById('check-audio-monitor'),

    // Pins Monitor
    pinsMonitorSource: document.getElementById('pins-monitor-source'),
    pinsMonitorAction: document.getElementById('pins-monitor-action'),
    btnConnectPinsHid: document.getElementById('btn-connect-pins-hid'),
    pinsMonitorHid: document.getElementById('pins-monitor-hid'),
    pinHidGpio1: document.getElementById('pin-hid-gpio1'),
    pinHidGpio2: document.getElementById('pin-hid-gpio2'),
    pinHidGpio3: document.getElementById('pin-hid-gpio3'),
    pinHidGpio4: document.getElementById('pin-hid-gpio4'),

    // Serial Console
    serialConsoleStatus: document.getElementById('serial-console-status'),
    serialAlert: document.getElementById('serial-alert'),
    selectSerialBaud: document.getElementById('select-serial-baud'),
    btnConnectSerialConsole: document.getElementById('btn-connect-serial-console'),
    serialTerminal: document.getElementById('serial-terminal'),
    inputSerialCmd: document.getElementById('input-serial-cmd'),
    btnSendSerialCmd: document.getElementById('btn-send-serial-cmd'),
    btnClearConsole: document.getElementById('btn-clear-console'),
    checkSendCr: document.getElementById('check-send-cr'),
    checkSendLf: document.getElementById('check-send-lf'),
  };

  // Perform support checks
  checkBrowserSupport();

  // Register event listeners
  initEventListeners();
});

// Check for WebHID, Web Serial and Web Audio support
function checkBrowserSupport() {
  const supportsHID = 'hid' in navigator;
  const supportsSerial = 'serial' in navigator;
  const supportsAudio = !!(window.AudioContext || window.webkitAudioContext);

  if (!supportsHID || !supportsSerial || !supportsAudio) {
    els.warningBanner.hidden = false;
    els.btnConnectPtt.disabled = true;
    els.btnConnectSerialConsole.disabled = true;
    els.btnConnectRxAudio.disabled = true;
  }
}

// Attach UI Event Handlers
function initEventListeners() {
  // PTT Mode Selector Change
  els.pttMode.addEventListener('change', (e) => {
    pttMode = e.target.value;
    if (pttMode === 'serial') {
      els.groupSerialPtt.hidden = false;
      els.groupHidPtt.hidden = true;
    } else {
      els.groupSerialPtt.hidden = true;
      els.groupHidPtt.hidden = false;
    }
    // Force disconnect on mode toggle to prevent resource conflicts
    disconnectPtt();
  });

  // Connect PTT Interface Button
  els.btnConnectPtt.addEventListener('click', () => {
    if (pttConnected) {
      disconnectPtt();
    } else {
      connectPtt();
    }
  });

  // PTT Trigger Button (Active Hold vs Toggle)
  els.btnPttTrigger.addEventListener('mousedown', () => {
    if (els.checkPttToggle.checked) return; // Managed by click in toggle mode
    setTransmitterState(true);
  });

  els.btnPttTrigger.addEventListener('mouseup', () => {
    if (els.checkPttToggle.checked) return;
    setTransmitterState(false);
  });

  els.btnPttTrigger.addEventListener('mouseleave', () => {
    if (els.checkPttToggle.checked) return;
    if (txActive) setTransmitterState(false);
  });

  els.btnPttTrigger.addEventListener('click', () => {
    if (!els.checkPttToggle.checked) return;
    setTransmitterState(!txActive);
  });

  // Keyboard Spacebar PTT Hotkey
  window.addEventListener('keydown', (e) => {
    // Avoid triggering PTT when user is typing in text inputs or command line
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.tagName === 'TEXTAREA')
    ) {
      return;
    }
    if (e.code === 'Space') {
      e.preventDefault(); // Prevent page scroll
      if (e.repeat) return;
      if (!pttConnected) return;

      if (els.checkPttToggle.checked) {
        setTransmitterState(!txActive);
      } else {
        if (!txActive) {
          setTransmitterState(true);
        }
      }
    }
  });

  window.addEventListener('keyup', (e) => {
    if (e.code === 'Space') {
      if (els.checkPttToggle.checked) return; // Toggle mode is managed on keydown
      if (pttConnected && txActive) {
        setTransmitterState(false);
      }
    }
  });

  // Tone Frequency Slider Change
  els.sliderToneFreq.addEventListener('input', (e) => {
    const val = parseFloat(e.target.value);
    els.inputToneFreq.value = val;
    updateToneFrequency(val);
  });

  // Tone Frequency Number Input Change
  els.inputToneFreq.addEventListener('input', (e) => {
    let val = parseFloat(e.target.value);
    if (isNaN(val)) return;
    // Bound values to range
    if (val < 100) val = 100;
    if (val > 3000) val = 3000;
    els.sliderToneFreq.value = val;
    updateToneFrequency(val);
  });

  function updateToneFrequency(val) {
    if (toneActive && toneOscillator) {
      toneOscillator.frequency.setValueAtTime(val, audioContext ? audioContext.currentTime : 0);
    }
  }

  // Send Tone Button
  els.btnSendTone.addEventListener('click', () => {
    sendTestTone();
  });

  // Send Morse Button
  els.btnSendMorse.addEventListener('click', () => {
    transmitMorseCallsign();
  });

  // Connect RX Audio Button
  els.btnConnectRxAudio.addEventListener('click', () => {
    if (rxAudioConnected) {
      disconnectRxAudio();
    } else {
      connectRxAudio();
    }
  });

  els.checkAudioMonitor.addEventListener('change', () => {
    if (rxMonitorGainNode) {
      const monitorOn = els.checkAudioMonitor.checked;
      rxMonitorGainNode.gain.value = monitorOn ? 2.0 : 0.0;
    }
  });

  // Connect HID Pins Monitor Button (for Serial PTT mode)
  els.btnConnectPinsHid.addEventListener('click', () => {
    if (monitorHidConnected) {
      disconnectHidMonitor();
    } else {
      connectHidMonitor();
    }
  });

  // Connect Serial Console Button
  els.btnConnectSerialConsole.addEventListener('click', () => {
    if (consoleSerialConnected) {
      disconnectSerialConsole();
    } else {
      connectSerialConsole();
    }
  });

  // Send Serial Command Button
  els.btnSendSerialCmd.addEventListener('click', () => {
    sendSerialCommand();
  });

  els.inputSerialCmd.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      sendSerialCommand();
    }
  });

  // Clear Console Output
  els.btnClearConsole.addEventListener('click', () => {
    els.serialTerminal.innerHTML = '';
  });

  // Enumerate Audio Devices on load only if permission is already granted.
  // Otherwise, delay to avoid prompting microphone permissions immediately.
  if (navigator.permissions && navigator.permissions.query) {
    navigator.permissions
      .query({ name: 'microphone' })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'granted') {
          enumerateAudioDevices();
        }
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            enumerateAudioDevices();
          }
        };
      })
      .catch((err) => {
        console.warn('Could not query microphone permission:', err);
      });
  }

  // Enumerate devices on focus of audio select dropdowns (only once)
  const handleAudioSelectFocus = () => {
    if (!audioDevicesEnumerated) {
      enumerateAudioDevices();
    }
  };
  els.selectAudioInput.addEventListener('focus', handleAudioSelectFocus);
  els.selectAudioOutput.addEventListener('focus', handleAudioSelectFocus);
}

/* ==========================================================================
   PTT Connections & State Control
   ========================================================================== */

// Helper to manage sharing a single serial port for PTT and Console
async function getOrRequestSerialPort(baudRate) {
  if (sharedSerialPort) {
    return sharedSerialPort;
  }
  const port = await navigator.serial.requestPort({
    filters: [{ usbVendorId: 0x1209, usbProductId: 0x7388 }], // AIOC VID/PID
  });
  await port.open({ baudRate });
  sharedSerialPort = port;
  return port;
}

// Helper to manage sharing a single HID device for PTT and Pin Monitor
async function getOrRequestHidDevice() {
  if (sharedHidDevice) {
    return sharedHidDevice;
  }

  const devices = await navigator.hid.requestDevice({
    filters: [
      { vendorId: 0x1209, productId: 0x7388 }, // AIOC
      { vendorId: 0x0d8c }, // Generic C-Media CM108
    ],
  });

  if (devices.length === 0) {
    throw new Error('No HID device selected.');
  }

  let targetDevice = null;
  for (const dev of devices) {
    const hasOutputReports =
      dev.collections && dev.collections.some((c) => c.outputReports && c.outputReports.length > 0);
    if (hasOutputReports) {
      targetDevice = dev;
      break;
    }
  }

  if (!targetDevice) {
    targetDevice = devices[0];
  }

  await targetDevice.open();
  sharedHidDevice = targetDevice;

  // Hook up the input report listener so we always catch GPIO reports
  sharedHidDevice.addEventListener('inputreport', handleHidInputReport);

  return sharedHidDevice;
}

async function connectPtt() {
  hideErrorAlert(els.pttAlert);
  els.btnConnectPtt.disabled = true;

  try {
    if (pttMode === 'serial') {
      // 1. Connect/reuse virtual serial port for PTT
      els.pttStatus.textContent = 'Requesting...';
      const port = await getOrRequestSerialPort(115200);

      pttSerialConnected = true;

      // Ensure transmitter is initially unkeyed
      await port.setSignals({ dataTerminalReady: false, requestToSend: false });

      els.pttStatus.textContent = 'Connected (Serial)';
      els.pttStatus.className = 'status-pill status-connected';
      els.btnConnectPtt.textContent = 'Disconnect PTT';
      els.btnConnectPtt.className = 'btn btn-muted';
      els.btnConnectPtt.style.boxShadow = 'none';
      els.btnConnectPtt.disabled = false;
      pttConnected = true;
      enableTxControls(true);
      if (els.pttSetupDetails) {
        els.pttSetupDetails.open = false;
      }
      showPinsMonitorType();
    } else if (pttMode === 'hid') {
      // 2. Connect to CM108 emulated HID interface for PTT
      els.pttStatus.textContent = 'Requesting...';
      const device = await getOrRequestHidDevice();

      pttHidConnected = true;

      // Ensure transmitter is unkeyed
      await setHidGpiostate(device, false);

      els.pttStatus.textContent = 'Connected (HID)';
      els.pttStatus.className = 'status-pill status-connected';
      els.btnConnectPtt.textContent = 'Disconnect PTT';
      els.btnConnectPtt.className = 'btn btn-muted';
      els.btnConnectPtt.style.boxShadow = 'none';
      els.btnConnectPtt.disabled = false;
      pttConnected = true;
      enableTxControls(true);
      if (els.pttSetupDetails) {
        els.pttSetupDetails.open = false;
      }
      showPinsMonitorType();
    }
  } catch (err) {
    console.error('PTT Connection Error:', err);
    showErrorAlert(els.pttAlert, `Failed to connect PTT interface: ${err.message}`);
    els.pttStatus.textContent = 'Disconnected';
    els.pttStatus.className = 'status-pill status-disconnected';
    els.btnConnectPtt.disabled = false;
    disconnectPtt();
  }
}

async function disconnectPtt() {
  // Turn off transmitter first
  if (txActive) {
    await setTransmitterState(false);
  }

  // Handle HID PTT disconnection
  if (pttHidConnected) {
    pttHidConnected = false;
    pttConnected = false;

    // Only close the HID device if monitor is also disconnected
    if (!monitorHidConnected && sharedHidDevice) {
      try {
        await setHidGpiostate(sharedHidDevice, false);
        sharedHidDevice.removeEventListener('inputreport', handleHidInputReport);
        await sharedHidDevice.close();
      } catch {
        // Ignored
      }
      sharedHidDevice = null;
    }
  }

  // Handle Serial PTT disconnection
  if (pttSerialConnected) {
    pttSerialConnected = false;
    pttConnected = false;
    if (sharedSerialPort) {
      try {
        await sharedSerialPort.setSignals({ dataTerminalReady: false, requestToSend: false });
      } catch {
        // Ignored
      }
      // Only close the port if console is also disconnected
      if (!consoleSerialConnected) {
        try {
          await sharedSerialPort.close();
        } catch {
          // Ignored
        }
        sharedSerialPort = null;
      }
    }
  }

  els.pttStatus.textContent = 'Disconnected';
  els.pttStatus.className = 'status-pill status-disconnected';
  els.btnConnectPtt.textContent = 'Connect PTT Interface';
  els.btnConnectPtt.className = 'btn btn-primary';
  els.btnConnectPtt.style.boxShadow = '';
  els.btnConnectPtt.disabled = false;
  enableTxControls(false);
  if (els.pttSetupDetails) {
    els.pttSetupDetails.open = true;
  }
  showPinsMonitorType();
}

/* ==========================================================================
   Hardware Pins & Squelch Monitoring
   ========================================================================== */

function showPinsMonitorType() {
  const isPttHid = pttConnected && pttMode === 'hid';

  if (isPttHid) {
    if (els.pinsMonitorAction) els.pinsMonitorAction.style.display = 'none';
    if (els.pinsMonitorHid) els.pinsMonitorHid.style.display = 'flex';
    if (els.pinsMonitorSource) {
      els.pinsMonitorSource.textContent = 'HID';
      els.pinsMonitorSource.className = 'status-pill status-connected';
    }
  } else if (monitorHidConnected) {
    if (els.pinsMonitorAction) els.pinsMonitorAction.style.display = 'block';
    if (els.btnConnectPinsHid) {
      els.btnConnectPinsHid.textContent = 'Disconnect HID Monitor';
      els.btnConnectPinsHid.className = 'btn btn-muted';
      els.btnConnectPinsHid.style.boxShadow = 'none';
    }
    if (els.pinsMonitorHid) els.pinsMonitorHid.style.display = 'flex';
    if (els.pinsMonitorSource) {
      els.pinsMonitorSource.textContent = 'HID';
      els.pinsMonitorSource.className = 'status-pill status-connected';
    }
  } else {
    if (els.pinsMonitorAction) els.pinsMonitorAction.style.display = 'block';
    if (els.btnConnectPinsHid) {
      els.btnConnectPinsHid.textContent = 'Connect HID Monitor';
      els.btnConnectPinsHid.className = 'btn btn-secondary';
      els.btnConnectPinsHid.style.boxShadow = '';
    }
    if (els.pinsMonitorHid) els.pinsMonitorHid.style.display = 'none';
    if (els.pinsMonitorSource) {
      els.pinsMonitorSource.textContent = 'Off';
      els.pinsMonitorSource.className = 'status-pill status-disconnected';
    }
    resetPinPills();
  }
}

async function connectHidMonitor() {
  hideErrorAlert(els.rxAlert);
  els.btnConnectPinsHid.disabled = true;

  try {
    await getOrRequestHidDevice();
    monitorHidConnected = true;
    showPinsMonitorType();
  } catch (err) {
    console.error('HID Monitor Connection Error:', err);
    showErrorAlert(els.rxAlert, `HID Monitor Error: ${err.message}`);
    disconnectHidMonitor();
  } finally {
    if (els.btnConnectPinsHid) {
      els.btnConnectPinsHid.disabled = false;
    }
  }
}

function disconnectHidMonitor() {
  monitorHidConnected = false;

  // Only close the HID device if PTT is also disconnected
  if (!pttHidConnected && sharedHidDevice) {
    try {
      sharedHidDevice.removeEventListener('inputreport', handleHidInputReport);
      sharedHidDevice.close();
    } catch {
      // Ignored
    }
    sharedHidDevice = null;
  }

  showPinsMonitorType();
}

function handleHidInputReport(event) {
  // Byte 1 of CM108 reports contains GPIO inputs in bits [3:0]
  if (event.data && event.data.byteLength > 1) {
    const gpioByte = event.data.getUint8(1);

    const gpio1 = (gpioByte & 0x01) !== 0;
    const gpio2 = (gpioByte & 0x02) !== 0;
    const gpio3 = (gpioByte & 0x04) !== 0;
    const gpio4 = (gpioByte & 0x08) !== 0;

    updatePinPill(els.pinHidGpio1, gpio1);
    updatePinPill(els.pinHidGpio2, gpio2);
    updatePinPill(els.pinHidGpio3, gpio3);
    updatePinPill(els.pinHidGpio4, gpio4);
  }
}

function updatePinPill(element, active) {
  if (!element) return;
  if (active) {
    element.className = 'pin-indicator active';
  } else {
    element.className = 'pin-indicator';
  }
}

function resetPinPills() {
  const pills = [els.pinHidGpio1, els.pinHidGpio2, els.pinHidGpio3, els.pinHidGpio4];
  pills.forEach((p) => {
    if (p) updatePinPill(p, false);
  });
}

// Direct interface hardware keying
async function setTransmitterState(active) {
  if (!pttConnected) return;

  try {
    if (pttMode === 'serial' && pttSerialConnected && sharedSerialPort) {
      const line = els.serialPttLine.value;
      let dtr = false;
      let rts = false;

      if (active) {
        if (line === 'DTR' || line === 'BOTH') dtr = true;
        if (line === 'RTS' || line === 'BOTH') rts = true;
      }
      await sharedSerialPort.setSignals({ dataTerminalReady: dtr, requestToSend: rts });
    } else if (pttMode === 'hid' && pttHidConnected && sharedHidDevice) {
      await setHidGpiostate(sharedHidDevice, active);
    }

    txActive = active;

    // Update UI states
    if (active) {
      els.btnPttTrigger.textContent = 'TRANSMITTING...';
      els.btnPttTrigger.classList.add('ptt-active');
      els.indicatorTxDot.classList.add('active');
    } else {
      els.btnPttTrigger.textContent = 'PRESS TO TALK (PTT)';
      els.btnPttTrigger.classList.remove('ptt-active');
      els.indicatorTxDot.classList.remove('active');
    }
  } catch (err) {
    console.error('PTT Toggle Error:', err);
    showErrorAlert(els.pttAlert, `PTT Error: ${err.message}`);
    disconnectPtt();
  }
}

// CM108 GPIO write formatting
async function setHidGpiostate(device, active) {
  const pin = els.hidPttPin.value;
  const pinMasks = {
    GPIO1: 0x01,
    GPIO2: 0x02,
    GPIO3: 0x04,
    GPIO4: 0x08,
  };
  const mask = pinMasks[pin] || 0x04;

  // CM108 Output Report format (GPIO control):
  // Byte 0: 0x00 (Report selector)
  // Byte 1: output level state (mask value for high, 0x00 for low)
  // Byte 2: output direction configuration (mask value sets pin as output)
  // Byte 3: 0x00 (unused/auxiliary)
  const reportData = new Uint8Array([0x00, active ? mask : 0x00, mask, 0x00]);

  // Send report to Endpoint ID 0
  await device.sendReport(0, reportData);
}

function enableTxControls(enabled) {
  els.btnPttTrigger.disabled = !enabled;

  if (!enabled) {
    els.btnPttTrigger.classList.remove('ptt-active');
    els.btnPttTrigger.textContent = 'PRESS TO TALK (PTT)';
    els.indicatorTxDot.classList.remove('active');
    els.checkPttToggle.checked = false;
    txActive = false;

    // Force stop tone generator on disconnect
    if (toneActive) {
      if (toneOscillator) {
        try {
          toneOscillator.stop();
        } catch {
          // Ignored
        }
        toneOscillator = null;
      }
      toneActive = false;
      els.btnSendTone.textContent = 'Start Test Tone';
    }

    // Force stop Morse code oscillator on disconnect
    if (morseOscillator) {
      try {
        morseOscillator.stop();
        morseOscillator.disconnect();
      } catch {
        // Ignored
      }
      morseOscillator = null;
    }
  }
}

/* ==========================================================================
   Audio Device Listing & Routing
   ========================================================================== */

async function enumerateAudioDevices() {
  try {
    // Prompt microphone access to fetch device labels (if not yet granted)
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);

    const devices = await navigator.mediaDevices.enumerateDevices();

    // Clear selections
    els.selectAudioInput.innerHTML = '';
    els.selectAudioOutput.innerHTML = '<option value="">-- Default System Audio --</option>';

    let inputCount = 0;
    devices.forEach((dev) => {
      if (dev.kind === 'audioinput') {
        const option = document.createElement('option');
        option.value = dev.deviceId;
        // Pre-select AIOC inputs if named clearly
        const label = dev.label || `Microphone ${++inputCount}`;
        option.textContent = label;
        if (label.toLowerCase().includes('aioc') || label.toLowerCase().includes('c-media')) {
          option.selected = true;
        }
        els.selectAudioInput.appendChild(option);
      } else if (dev.kind === 'audiooutput') {
        const option = document.createElement('option');
        option.value = dev.deviceId;
        const label = dev.label || 'Default Output';
        option.textContent = label;
        if (label.toLowerCase().includes('aioc') || label.toLowerCase().includes('c-media')) {
          option.selected = true;
        }
        els.selectAudioOutput.appendChild(option);
      }
    });

    // Close temporary stream if it was created
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    audioDevicesEnumerated = true;
  } catch (err) {
    console.warn('Could not enumerate audio devices:', err.message);
  }
}

/* ==========================================================================
   RX Audio Analysis & Squelch VU Meter
   ========================================================================== */

async function connectRxAudio() {
  hideErrorAlert(els.rxAlert);
  els.btnConnectRxAudio.disabled = true;

  try {
    if (!audioDevicesEnumerated) {
      await enumerateAudioDevices();
    }

    const inputId = els.selectAudioInput.value;
    if (!inputId) {
      throw new Error('Please select an audio input device.');
    }

    // Capture microphone input stream with raw settings (no echo cancel, AGC, or noise filters)
    rxStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: { exact: inputId },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

    audioContext = new (window.AudioContext || window.webkitAudioContext)({
      latencyHint: 'interactive',
    });

    rxSourceNode = audioContext.createMediaStreamSource(rxStream);
    rxAnalyserNode = audioContext.createAnalyser();
    rxAnalyserNode.fftSize = 256;
    rxSourceNode.connect(rxAnalyserNode);

    // Create monitor/loopback gain node
    rxMonitorGainNode = audioContext.createGain();
    const monitorOn = els.checkAudioMonitor.checked;
    rxMonitorGainNode.gain.value = monitorOn ? 2.0 : 0.0;

    rxSourceNode.connect(rxMonitorGainNode);
    rxMonitorGainNode.connect(audioContext.destination);

    els.rxStatus.textContent = 'Monitoring';
    els.rxStatus.className = 'status-pill status-connected';
    els.btnConnectRxAudio.textContent = 'Disconnect Audio';
    els.btnConnectRxAudio.disabled = false;
    rxAudioConnected = true;
    if (els.rxSetupDetails) {
      els.rxSetupDetails.open = false;
    }

    // Start VU Meter
    vuMeterActive = true;
    drawVUMeter();
  } catch (err) {
    console.error('RX Audio Connection Error:', err);
    showErrorAlert(els.rxAlert, `RX Audio Error: ${err.message}`);
    els.rxStatus.textContent = 'Disconnected';
    els.rxStatus.className = 'status-pill status-disconnected';
    els.btnConnectRxAudio.disabled = false;
    disconnectRxAudio();
  }
}

function disconnectRxAudio() {
  vuMeterActive = false;
  rxAudioConnected = false;

  if (rxStream) {
    rxStream.getTracks().forEach((track) => track.stop());
    rxStream = null;
  }

  if (rxSourceNode) {
    rxSourceNode.disconnect();
    rxSourceNode = null;
  }

  if (rxAnalyserNode) {
    rxAnalyserNode.disconnect();
    rxAnalyserNode = null;
  }

  if (rxMonitorGainNode) {
    rxMonitorGainNode.disconnect();
    rxMonitorGainNode = null;
  }

  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }

  // Clear Canvas VU display
  clearVUMeter();

  els.rxStatus.textContent = 'Disconnected';
  els.rxStatus.className = 'status-pill status-disconnected';
  els.btnConnectRxAudio.textContent = 'Connect RX Audio';
  els.btnConnectRxAudio.disabled = false;
  if (els.rxSetupDetails) {
    els.rxSetupDetails.open = true;
  }
}

function clearVUMeter() {
  const canvas = els.canvasVuMeter;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#2a2b2a'; // Deep charcoal background
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw zeroed grid line
  ctx.fillStyle = '#625f55';
  ctx.fillRect(5, canvas.height - 20, canvas.width - 10, 8);

  els.textVuDb.textContent = '0%';
}

function drawVUMeter() {
  if (!vuMeterActive || !rxAnalyserNode) return;

  requestAnimationFrame(drawVUMeter);

  const bufferLength = rxAnalyserNode.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);
  rxAnalyserNode.getByteTimeDomainData(dataArray);

  // 1. Calculate Signal RMS (root-mean-square) Power
  let sum = 0;
  for (let i = 0; i < bufferLength; i++) {
    const floatVal = (dataArray[i] - 128) / 128; // Normalize to [-1.0, 1.0]
    sum += floatVal * floatVal;
  }
  const rms = Math.sqrt(sum / bufferLength);

  // Scale RMS level for visual clarity (0 to 100%)
  const percentage = Math.min(100, Math.round(rms * 400));

  // Update text label with signal level percentage
  els.textVuDb.textContent = `Level: ${percentage}%`;

  // 3. Render VU Meter Graphics
  const canvas = els.canvasVuMeter;
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;

  // Clean background
  ctx.fillStyle = '#2a2b2a';
  ctx.fillRect(0, 0, width, height);

  // Draw divisions / scale ticks
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  for (let i = 1; i < 10; i++) {
    const tickX = Math.round((width - 20) * (i / 10)) + 10;
    ctx.fillRect(tickX, 5, 2, height - 10);
  }

  // Calculate bar sizes
  const barWidth = Math.round((width - 20) * (percentage / 100));

  // Fill gradient depending on signal levels
  if (barWidth > 0) {
    const grad = ctx.createLinearGradient(10, 0, width - 10, 0);
    grad.addColorStop(0, '#10b981'); // Green
    grad.addColorStop(0.6, '#f59e0b'); // Yellow
    grad.addColorStop(0.85, '#ef4444'); // Red
    ctx.fillStyle = grad;
    ctx.fillRect(10, 15, barWidth, height - 30);
  }

  // Peak hold logic
  if (percentage >= peakLevel) {
    peakLevel = percentage;
    peakHoldFrames = 30; // Hold for ~0.5s
  } else {
    if (peakHoldFrames > 0) {
      peakHoldFrames--;
    } else {
      peakLevel -= 1.5; // Smooth decay
      if (peakLevel < 0) peakLevel = 0;
    }
  }

  // Draw Peak marker line
  if (peakLevel > 0) {
    const peakX = Math.round((width - 20) * (peakLevel / 100)) + 10;
    ctx.fillStyle = '#ef4444'; // Red peak marker
    ctx.fillRect(peakX, 8, 3, height - 16);
  }
}

/* ==========================================================================
   TX Audio Tone & Morse Transmissions
   ========================================================================== */

async function prepareTxAudioContext() {
  // Re-use or initialize separate audio context for TX to prevent overlap
  let txContext = audioContext;
  if (!txContext) {
    txContext = new (window.AudioContext || window.webkitAudioContext)();
  }

  // Route output device if selected (SinkID)
  const outputId = els.selectAudioOutput.value;
  if (outputId && typeof txContext.setSinkId === 'function') {
    await txContext.setSinkId(outputId);
  }

  return txContext;
}

async function sendTestTone() {
  hideErrorAlert(els.pttAlert);

  if (toneActive) {
    // Stop the active test tone
    try {
      if (toneGainNode && audioContext) {
        // Smooth fade out to prevent clicks
        toneGainNode.gain.setValueAtTime(toneGainNode.gain.value, audioContext.currentTime);
        toneGainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.05);
      }
      setTimeout(() => {
        if (toneOscillator) {
          try {
            toneOscillator.stop();
            toneOscillator.disconnect();
          } catch {
            // Ignored
          }
          toneOscillator = null;
        }
        if (toneGainNode) {
          try {
            toneGainNode.disconnect();
          } catch {
            // Ignored
          }
          toneGainNode = null;
        }
        toneActive = false;
        els.btnSendTone.textContent = 'Start Test Tone';
      }, 60);
    } catch (err) {
      console.error('Error stopping test tone:', err);
      toneActive = false;
      els.btnSendTone.textContent = 'Start Test Tone';
    }
  } else {
    // Start the continuous test tone
    try {
      const txContext = await prepareTxAudioContext();
      const frequency = parseFloat(els.sliderToneFreq.value);

      // Play continuous oscillator tone
      const osc = txContext.createOscillator();
      const gainNode = txContext.createGain();

      osc.type = 'sine';
      osc.frequency.value = frequency;

      gainNode.gain.setValueAtTime(0.001, txContext.currentTime); // fade-in
      gainNode.gain.exponentialRampToValueAtTime(0.8, txContext.currentTime + 0.05); // full volume

      osc.connect(gainNode);
      gainNode.connect(txContext.destination);

      osc.start();

      toneOscillator = osc;
      toneGainNode = gainNode;
      toneActive = true;

      els.btnSendTone.textContent = 'Stop Test Tone';
    } catch (err) {
      console.error('Tone Generation Error:', err);
      showErrorAlert(els.pttAlert, `TX Tone Error: ${err.message}`);
      toneActive = false;
      els.btnSendTone.textContent = 'Start Test Tone';
    }
  }
}

async function transmitMorseCallsign() {
  hideErrorAlert(els.pttAlert);
  els.btnSendMorse.disabled = true;

  try {
    const txContext = await prepareTxAudioContext();
    const textMsg = els.inputMorseMsg.value.toUpperCase();
    const wpm = parseInt(els.inputMorseWpm.value, 10);

    // Calculate Morse timing unit lengths (WPM formula: Dot time = 1.2 / WPM seconds)
    const dotLength = 1.2 / wpm;

    // Convert text string to Morse symbol representation
    let morseCode = '';
    for (let char of textMsg) {
      const code = MORSE_MAP[char];
      if (code) {
        morseCode += code + ' ';
      }
    }
    morseCode = morseCode.trim();

    if (!morseCode) {
      throw new Error('No valid alphanumeric characters found to transmit.');
    }

    // 1. Key transmitter (PTT active)
    await setTransmitterState(true);

    // Wait RX-to-TX turnaround delay (200ms)
    await new Promise((resolve) => setTimeout(resolve, 200));

    // 2. Schedule Morse signal tones
    if (morseOscillator) {
      try {
        morseOscillator.stop();
        morseOscillator.disconnect();
      } catch {
        // Ignored
      }
      morseOscillator = null;
    }

    morseOscillator = txContext.createOscillator();
    const gainNode = txContext.createGain();

    morseOscillator.type = 'sine';
    morseOscillator.frequency.value = 800; // Standard 800Hz side tone for Morse
    gainNode.gain.setValueAtTime(0.0, txContext.currentTime);

    morseOscillator.connect(gainNode);
    gainNode.connect(txContext.destination);

    // Schedule sequence on AudioContext timeline to prevent javascript setInterval jitter (with 50ms safety margin)
    let schedTime = txContext.currentTime + 0.05;
    morseOscillator.start(schedTime);

    for (let i = 0; i < morseCode.length; i++) {
      const symbol = morseCode[i];

      if (symbol === '.') {
        // Dot: 1 unit on, 1 unit off
        gainNode.gain.setValueAtTime(0.0, schedTime);
        gainNode.gain.linearRampToValueAtTime(0.8, schedTime + 0.005); // quick ramp
        schedTime += dotLength;
        gainNode.gain.setValueAtTime(0.8, schedTime);
        gainNode.gain.linearRampToValueAtTime(0.0, schedTime + 0.005);
        schedTime += dotLength; // intra-element space
      } else if (symbol === '-') {
        // Dash: 3 units on, 1 unit off
        gainNode.gain.setValueAtTime(0.0, schedTime);
        gainNode.gain.linearRampToValueAtTime(0.8, schedTime + 0.005);
        schedTime += dotLength * 3;
        gainNode.gain.setValueAtTime(0.8, schedTime);
        gainNode.gain.linearRampToValueAtTime(0.0, schedTime + 0.005);
        schedTime += dotLength; // intra-element space
      } else if (symbol === ' ') {
        // Space between letters: 3 units total (we already have 1 unit from intra-element space)
        schedTime += dotLength * 2;
      } else if (symbol === '/') {
        // Space between words: 7 units total (we already have 1 unit from intra-element space)
        schedTime += dotLength * 6;
      }
    }

    // Stop oscillator after sequence completes
    morseOscillator.stop(schedTime + 0.1);

    // 3. Release PTT once Morse completes
    morseOscillator.onended = async () => {
      setTimeout(async () => {
        await setTransmitterState(false);
        els.btnSendMorse.disabled = false;
        morseOscillator = null;
      }, 100);
    };
  } catch (err) {
    console.error('Morse Transmission Error:', err);
    showErrorAlert(els.pttAlert, `Morse TX Error: ${err.message}`);
    await setTransmitterState(false);
    els.btnSendMorse.disabled = false;
    morseOscillator = null;
  }
}

/* ==========================================================================
   Serial Console
   ========================================================================== */

async function connectSerialConsole() {
  hideErrorAlert(els.serialAlert);
  els.btnConnectSerialConsole.disabled = true;

  try {
    const baudRate = parseInt(els.selectSerialBaud.value, 10);

    els.serialConsoleStatus.textContent = 'Requesting...';
    // Use the shared helper to connect or reuse the open serial port
    await getOrRequestSerialPort(baudRate);

    consoleSerialConnected = true;

    els.serialConsoleStatus.textContent = 'Connected';
    els.serialConsoleStatus.className = 'status-pill status-connected';
    els.btnConnectSerialConsole.textContent = 'Disconnect Console';
    els.btnConnectSerialConsole.disabled = false;
    els.inputSerialCmd.disabled = false;
    els.btnSendSerialCmd.disabled = false;

    // Clear terminal and append welcome line
    els.serialTerminal.innerHTML = `<span style="color:#808180;">--- Serial Console Connected @ ${baudRate} bps ---</span>\n`;

    // Start background read loop
    readSerialLoop();
  } catch (err) {
    console.error('Serial Console Connection Error:', err);
    showErrorAlert(els.serialAlert, `Failed to open console: ${err.message}`);
    els.serialConsoleStatus.textContent = 'Disconnected';
    els.serialConsoleStatus.className = 'status-pill status-disconnected';
    els.btnConnectSerialConsole.disabled = false;
    disconnectSerialConsole();
  }
}

async function disconnectSerialConsole() {
  consoleSerialConnected = false;

  // Cancel active reader stream
  if (serialConsoleReader) {
    try {
      await serialConsoleReader.cancel();
    } catch {
      // Ignored
    }
    serialConsoleReader = null;
  }

  // Close Serial Port if PTT is not using it
  if (sharedSerialPort) {
    if (!pttSerialConnected) {
      try {
        await sharedSerialPort.close();
      } catch {
        // Ignored
      }
      sharedSerialPort = null;
    }
  }

  els.serialConsoleStatus.textContent = 'Disconnected';
  els.serialConsoleStatus.className = 'status-pill status-disconnected';
  els.btnConnectSerialConsole.textContent = 'Connect Console';
  els.btnConnectSerialConsole.disabled = false;
  els.inputSerialCmd.disabled = true;
  els.btnSendSerialCmd.disabled = true;

  els.serialTerminal.innerHTML += `<span style="color:#808180;">\n--- Serial Console Disconnected ---</span>\n`;
}

// Read incoming serial bytes asynchronously
async function readSerialLoop() {
  const decoder = new TextDecoder();

  while (consoleSerialConnected && sharedSerialPort && sharedSerialPort.readable) {
    try {
      serialConsoleReader = sharedSerialPort.readable.getReader();

      while (true) {
        const { value, done } = await serialConsoleReader.read();
        if (done) break;
        if (value) {
          const text = decoder.decode(value);
          appendConsoleOutput(text);
        }
      }
    } catch (err) {
      // Don't show error if we intentionally disconnected
      if (consoleSerialConnected) {
        console.error('Serial Read Loop Error:', err);
        showErrorAlert(els.serialAlert, `Serial Read Error: ${err.message}`);
        disconnectSerialConsole();
      }
      break;
    } finally {
      if (serialConsoleReader) {
        serialConsoleReader.releaseLock();
        serialConsoleReader = null;
      }
    }
  }
}

// Send Command via Serial Console
async function sendSerialCommand() {
  if (!consoleSerialConnected || !sharedSerialPort || !sharedSerialPort.writable) return;

  const cmdInput = els.inputSerialCmd.value;
  if (!cmdInput) return;

  els.inputSerialCmd.disabled = true;
  els.btnSendSerialCmd.disabled = true;

  try {
    let finalCmd = cmdInput;
    if (els.checkSendCr.checked) finalCmd += '\r';
    if (els.checkSendLf.checked) finalCmd += '\n';

    const writer = sharedSerialPort.writable.getWriter();
    const encoder = new TextEncoder();

    // Echo command in green log line
    appendConsoleOutput(`\n> ${cmdInput}\n`);

    await writer.write(encoder.encode(finalCmd));
    writer.releaseLock();

    els.inputSerialCmd.value = '';
  } catch (err) {
    console.error('Serial Console Send Error:', err);
    showErrorAlert(els.serialAlert, `Failed to send command: ${err.message}`);
    disconnectSerialConsole();
  } finally {
    if (consoleSerialConnected) {
      els.inputSerialCmd.disabled = false;
      els.btnSendSerialCmd.disabled = false;
      els.inputSerialCmd.focus();
    }
  }
}

function appendConsoleOutput(text) {
  const term = els.serialTerminal;

  // Escape raw HTML tags to prevent cross-site execution
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  term.innerHTML += escaped;

  // Cap console log content length (max 8000 chars) to prevent DOM performance slowdowns
  if (term.innerHTML.length > 8000) {
    term.innerHTML = term.innerHTML.substring(term.innerHTML.length - 6000);
  }

  // Scroll to bottom
  term.scrollTop = term.scrollHeight;
}

/* ==========================================================================
   Error Display Helpers
   ========================================================================== */

function showErrorAlert(element, message) {
  if (element) {
    element.textContent = message;
    element.hidden = false;
  }
}

function hideErrorAlert(element) {
  if (element) {
    element.hidden = true;
    element.textContent = '';
  }
}
